import { useCallback, useState } from 'react';
import { storage } from '../services/storageService';
import { HealthEntry, Reminder } from '../types';

type UpdateUsage = (usage: { currentLogs?: number; currentReminders?: number }) => Promise<void>;

type UseLogsRemindersOptions = {
  updateUsage?: UpdateUsage;
  onLimitsChanged?: () => void;
};

export const useLogsReminders = (options: UseLogsRemindersOptions = {}) => {
  const { updateUsage, onLimitsChanged } = options;

  const [history, setHistory] = useState<HealthEntry[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(false);

  const reloadPetData = useCallback(async (petId: string) => {
    setLoading(true);
    try {
      const [logs, rems] = await Promise.all([
        storage.getLogs(petId),
        storage.getReminders(petId)
      ]);
      setHistory(logs);
      setReminders(rems);
      return { logs, rems };
    } finally {
      setLoading(false);
    }
  }, []);

  const saveLog = useCallback(async (petId: string, log: Omit<HealthEntry, 'id'>) => {
    await storage.saveLog(log);
    const { logs } = await reloadPetData(petId);

    if (updateUsage) {
      await updateUsage({ currentLogs: logs.length });
    }
    onLimitsChanged?.();

    return logs;
  }, [onLimitsChanged, reloadPetData, updateUsage]);

  const deleteLog = useCallback(async (petId: string, logId: string) => {
    await storage.deleteLog(logId);
    const { logs } = await reloadPetData(petId);

    if (updateUsage) {
      await updateUsage({ currentLogs: logs.length });
    }
    onLimitsChanged?.();

    return logs;
  }, [onLimitsChanged, reloadPetData, updateUsage]);

  const saveReminder = useCallback(async (petId: string, reminder: Omit<Reminder, 'id'>) => {
    await storage.saveReminder(reminder);
    const { rems } = await reloadPetData(petId);

    if (updateUsage) {
      await updateUsage({ currentReminders: rems.length });
    }
    onLimitsChanged?.();

    return rems;
  }, [onLimitsChanged, reloadPetData, updateUsage]);

  const deleteReminder = useCallback(async (petId: string, reminderId: string) => {
    await storage.deleteReminder(reminderId);
    const { rems } = await reloadPetData(petId);

    if (updateUsage) {
      await updateUsage({ currentReminders: rems.length });
    }
    onLimitsChanged?.();

    return rems;
  }, [onLimitsChanged, reloadPetData, updateUsage]);

  return {
    history,
    reminders,
    loading,
    reloadPetData,
    saveLog,
    deleteLog,
    saveReminder,
    deleteReminder,
    setHistory,
    setReminders
  };
};
